// ============================================================
// SheetSection - one section of the sheet: a stencil caption over a
// hairline, and optionally a fold.
//
// A folded section is its caption and a one-line summary, so a sheet
// three panels long reads at a glance. Whether a section is folded is
// the VIEWER's own preference (never synced), remembered per section.
// ============================================================
import { useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { getViewerPrefs, setViewerPref, subscribeViewerPrefs } from '../../utils/viewerPrefs';
import type { SheetSectionId } from '../../engine/roster/pins';
import styles from './Sheet.module.css';

const PREF_KEY = 'sheetFolds';

/**
 * The sections a viewer may fold, which are also the ones that START
 * folded. The core of the sheet (characteristics, vitals, armour,
 * traits) is always open.
 */
export const FOLDABLE: ReadonlySet<SheetSectionId> = new Set(['defence', 'movement', 'talents', 'skills', 'stances']);

/** Whether each section is folded for this viewer, and the toggle. */
export function useSheetFolds() {
  const prefs = useSyncExternalStore(subscribeViewerPrefs, getViewerPrefs) as Record<string, unknown>;
  const stored = (prefs[PREF_KEY] ?? {}) as Partial<Record<SheetSectionId, boolean>>;
  const isFolded = (id: SheetSectionId) => stored[id] ?? FOLDABLE.has(id);
  const toggle = (id: SheetSectionId) => setViewerPref(PREF_KEY, { ...stored, [id]: !isFolded(id) });
  return { isFolded, toggle };
}

interface SheetSectionProps {
  id: SheetSectionId;
  title: string;
  /** One line that stands in for the body while folded, and sits beside the title when open. */
  summary?: ReactNode;
  /** Something at the caption's right edge: "+ ADD". Never inside the fold button. */
  action?: ReactNode;
  folded?: boolean;
  onToggle?: () => void;
  children: ReactNode;
}

export default function SheetSection({ id, title, summary, action, folded, onToggle, children }: SheetSectionProps) {
  const caption = (
    <>
      <span className={styles.captionTitle}>{title}</span>
      {summary && <span className={styles.captionSummary}>{summary}</span>}
      <span className={styles.captionRule} />
    </>
  );
  return (
    <section className={styles.section} data-testid={`sheet-section-${id}`}>
      <div className={styles.captionRow}>
        {onToggle ? (
          <button type="button" className={styles.caption} aria-expanded={!folded} onClick={onToggle}>
            {caption}
            <span className={styles.captionFold}>{folded ? 'OPEN' : 'FOLD'}</span>
          </button>
        ) : (
          <div className={styles.caption}>{caption}</div>
        )}
        {action}
      </div>
      {!folded && <div className={styles.sectionBody}>{children}</div>}
    </section>
  );
}
